import { useQuery } from '@tanstack/react-query';
import { downloadImage } from 'shared/services/backend-api';
import { getErrorReporter } from 'shared/services/error-tracking';
import { ID } from 'shared/types/id';

export const queryKey = 'download-bucket-image';

// Failed resolutions degrade to a silent placeholder in the UI, so report once per url per session.
const reportedUrls = new Set<string>();

export const resolveBucketImageUrl = async (src: string): Promise<string> => {
  try {
    return await downloadImage(src);
  } catch (error) {
    if (!reportedUrls.has(src)) {
      reportedUrls.add(src);
      const code = (error as { code?: string }).code ?? 'unknown';
      getErrorReporter().captureException(error, {
        tags: { source: 'storage-url', code },
        extra: { src },
        fingerprint: ['storage-url', code],
      });
    }
    throw error;
  }
};

export const useDownloadBucketImageQuery = (uid: ID = '', src?: string) => {
  const isBucketLink = !!src?.startsWith('gs://');

  const result = useQuery({
    queryKey: [queryKey, uid, src],
    queryFn: () => resolveBucketImageUrl(src!),
    enabled: !!uid && isBucketLink,
  });

  return {
    ...result,
    isBucketLink,
  };
};
